import { computed, effect, inject, Injectable, signal, untracked } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { LicensesRepositoryService } from '../../repositories/ecommerce/licenses.repository.service';
import { ActivePlanModel, LicenseModel } from '../../models/ecommerce/license.model';
import { EcommerceAuthService } from './ecommerce-auth.service';
import { AppRoutes } from '../../models/routes.model';
import {
  isOpenLicenseState,
  licenseStateLabel,
} from '../../utils/ecommerce/license-state.util';

const PAGE_SIZE = 100;
const MAX_LICENSES = 500;

/**
 * Piani del cliente, ricavati dalle licenze (`GET /licenses`). Prima si
 * deducevano dagli ordini leggendone uno per uno il dettaglio; la licenza
 * porta già piano, stato e soprattutto `access_allowed`, che il backend indica
 * come unica fonte per autorizzare l'accesso.
 */
@Injectable({
  providedIn: 'root',
})
export class ActivePlansService {
  private readonly repo = inject(LicensesRepositoryService);
  private readonly ecommerceAuthService = inject(EcommerceAuthService);

  private readonly plansSignal = signal<ActivePlanModel[]>([]);
  private readonly loadingSignal = signal<boolean>(false);
  private readonly errorSignal = signal<unknown>(null);
  private loadedForSession = false;

  readonly plans = computed(() => this.plansSignal());
  readonly isLoading = computed(() => this.loadingSignal());
  readonly error = computed(() => this.errorSignal());

  /**
   * Un piano attivo esclude un nuovo acquisto per la stessa applicazione: non
   * esiste ancora un flusso di rinnovo o di upgrade, quindi un secondo ordine
   * creerebbe due licenze sovrapposte. Chi ha già acquistato deve passare dal
   * pannello di gestione.
   */
  readonly hasActivePlan = computed(() => this.plansSignal().length > 0);

  /**
   * Piano da mostrare quando l'acquisto è bloccato: quello con accesso, se
   * c'è, altrimenti il più recente.
   */
  readonly currentPlan = computed<ActivePlanModel | null>(
    () => this.manageablePlans()[0] ?? this.plansSignal()[0] ?? null
  );

  /**
   * Piani con accesso consentito. Il pannello di gestione si apre solo su
   * questi: una licenza in attivazione o sospesa blocca un nuovo acquisto ma
   * non dà accesso all'applicazione.
   */
  readonly manageablePlans = computed(() =>
    this.plansSignal().filter((plan) => plan.accessAllowed)
  );
  readonly hasManageablePlan = computed(() => this.manageablePlans().length > 0);

  /**
   * Oggi il catalogo vende solo Nfarm, quindi ogni piano riguarda
   * l'applicazione di default. La licenza porta `app_code`, ma finché il
   * valore restituito non è verificato non si filtra: un codice diverso da
   * `nfarm` sbloccherebbe un secondo acquisto.
   */
  hasPlanForApp(appCode: string = AppRoutes.DEFAULT_APP_CODE): boolean {
    void appCode;
    return this.hasActivePlan();
  }

  constructor() {
    // Al login (o al riaggancio SSO) si caricano i piani; al logout si svuotano.
    effect(() => {
      const authenticated = this.ecommerceAuthService.isAuthenticated();
      untracked(() => {
        if (authenticated) {
          void this.ensureLoaded();
        } else {
          this.reset();
        }
      });
    });
  }

  /** Carica una sola volta per sessione; `reload()` forza il refresh. */
  async ensureLoaded(): Promise<void> {
    if (this.loadedForSession || this.loadingSignal()) {
      return;
    }
    await this.reload();
  }

  async reload(): Promise<void> {
    if (!this.ecommerceAuthService.isAuthenticated()) {
      this.reset();
      return;
    }

    this.loadingSignal.set(true);
    this.errorSignal.set(null);
    try {
      const licenses = await this.listAllLicenses();
      const plans = licenses
        .filter((license) => isOpenLicenseState(license.state))
        .map((license) => this.toActivePlan(license))
        .sort((a, b) => this.compareByStartDesc(a, b));

      this.plansSignal.set(plans);
      this.loadedForSession = true;
    } catch (error) {
      this.errorSignal.set(error);
    } finally {
      this.loadingSignal.set(false);
    }
  }

  private async listAllLicenses(): Promise<LicenseModel[]> {
    const collected: LicenseModel[] = [];
    let offset = 0;
    let total = 0;
    do {
      const response = await firstValueFrom(this.repo.listLicenses(PAGE_SIZE, offset));
      collected.push(...response.items);
      total = response.total;
      offset += response.items.length;
      if (!response.items.length) {
        break;
      }
    } while (collected.length < Math.min(total, MAX_LICENSES));
    return collected;
  }

  private toActivePlan(license: LicenseModel): ActivePlanModel {
    const configuration = license.configuration;
    return {
      licenseId: license.license_id,
      appCode: license.app_code,
      reference: license.subscription_reference,
      planCode: license.plan_code ?? configuration?.plan_code ?? null,
      planName:
        configuration?.public_name ||
        license.plan_name ||
        configuration?.plan_name ||
        license.subscription_reference,
      state: license.state,
      stateLabel: licenseStateLabel(license.state),
      accessAllowed: license.access_allowed,
      isTrial: license.is_trial,
      dateFrom: license.date_from,
      accessUntil: license.access_until,
    };
  }

  /** Più recente prima; una licenza ancora senza data (in attivazione) è la più nuova. */
  private compareByStartDesc(a: ActivePlanModel, b: ActivePlanModel): number {
    if (!a.dateFrom || !b.dateFrom) {
      return (a.dateFrom ? 1 : 0) - (b.dateFrom ? 1 : 0);
    }
    return new Date(b.dateFrom).getTime() - new Date(a.dateFrom).getTime();
  }

  private reset(): void {
    this.plansSignal.set([]);
    this.errorSignal.set(null);
    this.loadedForSession = false;
  }
}
